import R from 'core-js/stable'; // https://stackoverflow.com/a/73775845/2361752
// eslint-disable-next-line no-unused-expressions
R;

import 'abortcontroller-polyfill/dist/polyfill-patch-fetch';
import React from 'react';
import 'bootstrap/dist/css/bootstrap.css';
import './custom.scss';
import './index.css';
import App from './modules/App';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Provider } from 'react-redux';
import store from './store';
import { createRoot } from 'react-dom/client';

import i18nInit from './localizations';

import packageJson from './_package.json';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { useWebSocket } from './websocket/hooks';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import reportWebVitals from './reportWebVitals';

const development =
  window.location.hostname.includes('localhost') ||
  window.location.hostname.includes('dev.datalouder');

if (development) {
  // eslint-disable-next-line no-console
  console.log('Skip loading sentry');
} else {
  // eslint-disable-next-line no-console
  console.log('Loading sentry');
  Sentry.init({
    dsn: 'https://d7d52534f3cc4a0a825a8875321e2592@sentry.io/5172638',
    release: 'frontend@' + packageJson.version,
    integrations: [
      new Integrations.BrowserTracing({
        routingInstrumentation: Sentry.reactRouterV6Instrumentation(
          React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes
        ),
      }),
    ],
    tracesSampleRate: 0.1,
    // eslint-disable-next-line sonarjs/cognitive-complexity
    beforeSend(event) {
      // eslint-disable-next-line sonarjs/no-collapsible-if
      if (
        event &&
        event.exception &&
        event.exception.values &&
        // eslint-disable-next-line sonarjs/cognitive-complexity
        event.exception.values.some(e => {
          // null is not an object (evaluating 'e.ownerSVGElement')
          // https://sentry.io/organizations/datalouder-zi/issues/1578386672/?project=5172638&query=&statsPeriod=14d
          if (
            e.type === 'TypeError' &&
            (e.value ===
              `null is not an object (evaluating 'e.ownerSVGElement')` ||
              e.value === `Cannot read property 'ownerSVGElement' of null`)
          ) {
            return true;
          }

          // stockcharts: undefined is not an object (evaluating 'n(e[a]).valueOf')
          // and https://sentry.io/organizations/datalouder-zi/issues/1578386341/
          // and https://sentry.io/organizations/datalouder-zi/issues/1752819857/
          if (
            e.type === 'TypeError' &&
            (e.value ===
              `undefined is not an object (evaluating 'n(e[a]).valueOf')` ||
              e.value === "Cannot read property 'valueOf' of undefined" ||
              e.value === 'n is not a function')
          ) {
            return true;
          }

          // Window closed thrown by paypal is also annoying
          if (e.type === 'Error' && e.value === `Window closed`) {
            return true;
          }

          // Paypal logger blocked
          if (
            e.type === 'Error' &&
            e.value &&
            e.value.includes(
              'https://www.paypal.com/xoplatform/logger/api/logger'
            )
          ) {
            return true;
          }

          // tsparticle
          if (
            e.type === 'TypeError' &&
            e.value === "Cannot read property 'nextFrame' of undefined"
          ) {
            return true;
          }

          // load resources failed
          if (e.value && /Loading CSS chunk \d+ failed./.test(e.value)) {
            return true;
          }

          // Detected container element removed from DOM
          // https://github.com/paypal/paypal-checkout-components/issues/1506
          if (
            e.type === 'Error' &&
            e.value === 'Detected container element removed from DOM'
          ) {
            return true;
          }

          // paypal sdk Maximum call stack size exceeded
          if (
            e.type === 'RangeError' &&
            e.value === 'Maximum call stack size exceeded' &&
            e.stacktrace &&
            JSON.stringify(e.stacktrace).includes('paypal.com/sdk')
          ) {
            return true;
          }

          // paypal zoid destroyed all components
          if (
            e.value === 'zoid destroyed all components' &&
            e.stacktrace &&
            JSON.stringify(e.stacktrace).includes('paypal.com/sdk')
          ) {
            return true;
          }

          // ignore all react-stockchart internal errors
          if (
            e.stacktrace &&
            JSON.stringify(e.stacktrace).includes('react-stockcharts')
          ) {
            return true;
          }

          // requests are aborted when browser got refreshed
          if (e.type === 'AbortError') {
            return true;
          }

          // DLError.NetworkError happens due to various reasons and they are really nosiy
          if (e.type === 'NetworkError') {
            return true;
          }

          // Microsoft clarity
          if (
            e.stacktrace &&
            JSON.stringify(e.stacktrace).includes('clarity.js')
          ) {
            return true;
          }

          // use request service error (usually new client available)
          return (
            e.type === 'UnhandledRejection' &&
            JSON.stringify(e.value).includes(
              'useRequest has caught the exception'
            )
          );
        })
      ) {
        return null;
      }

      return event;
    },
  });
}

const AppWithWebSocket: React.FC = React.memo(() => {
  useWebSocket();
  return <App />;
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Number.POSITIVE_INFINITY,
      retry: false, // TODO: makes it retry for 500 errors
    },
  },
});

i18nInit(store.getState().preference.lang);
const Root: React.FC = () => (
  <Provider store={store}>
    <QueryClientProvider client={queryClient}>
      <AppWithWebSocket />
    </QueryClientProvider>
  </Provider>
);

const root = createRoot(document.querySelector('#root')!);
root.render(<Root />);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
